import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {get} from 'lodash';
import {from, merge, Observable, of} from 'rxjs';
import {filter, flatMap, map, tap} from 'rxjs/operators';
import {environment} from '../../environments/environment';

@Injectable({
	providedIn: 'root'
})
export class CourseDataService {

	/**
	 * Parse the response from Kentico's response into a more standard JS Object
	 * @param item The item from Kentico
	 * @param modularContent The Modular Content from Kentico response
	 */
	static mapKenticoObject(item, modularContent) {
		const newObj = {} as any;

		newObj.system_id = item.system.id;
		newObj.system_type = item.system.type;
		newObj.system_name = item.system.name;
		newObj.system_codename = item.system.codename;
		newObj.system_last_modified = item.system.last_modified;
		Object.keys(item.elements).forEach(k => {
			if (!item.elements[k]) {
				newObj[k] = null;
			} else if (item.elements[k].type === 'modular_content') {
				newObj[k] = item.elements[k].value.map(modK => {
					if (modularContent && modularContent[modK]) {
						return CourseDataService.mapKenticoObject(modularContent[modK], modularContent);
					} else {
						return modK;
					}
				});
			} else if (Array.isArray(item.elements[k].value) && item.elements[k].type === 'asset') {
				newObj[k] = item.elements[k].value;
			} else {
				// TODO handle multiple_choice
				// TODO do we need name?

				//TODO is useful or does ngrx handle this for me?
				if (k.endsWith('_date') && typeof item.elements[k].value === 'string') {
					try {
						const newDate = new Date(item.elements[k].value);
						if (newDate instanceof Date && !isNaN(newDate as any)) {
							newObj[k] = newDate;
							return;
						}
					} catch (e) {
						console.log(`unable to parse ${k} (${item.elements[k].value} to date`);
					}
				}

				newObj[k] = item.elements[k].value;
			}
		});

		return newObj;
	}

	static getRequestOptions() {
		return {params: new HttpParams(), headers: new HttpHeaders().append('Authorization', `Bearer ${environment.kenticoToken}`)}
	}

	constructor(private httpService: HttpClient) { }

	getCourse(itemId: string) {
		if (environment.production) {
			return this.httpService.get<any>(`${environment.kenticoRoot}/item-${itemId}.json`).pipe(
				map(r => new Course(r))
			);
		} else {

			const options = CourseDataService.getRequestOptions();
			options.params = options.params.append('system.type', 'course');
			return this.httpService.get<any>(`${environment.kenticoRoot}/items/${itemId}`, options).pipe(
				map(r => environment.production ? r : CourseDataService.mapKenticoObject(r.item, r.modular_content)),
				map(r => new Course(r))
			);
		}
	}
	getCourses() {
		if (environment.production) {
			return this.httpService.get<any>(`${environment.kenticoRoot}/items.json`).pipe(
				map(res => res.map(r => new Course(r)))
			);
		} else {
			const options = CourseDataService.getRequestOptions();
			options.params = options.params
				.append('system.type', 'course')
				.append('depth', '3');
			return this.httpService.get<any>(`${environment.kenticoRoot}/items`, options).pipe(
				map(r => environment.production ? r : r.items.map(i => CourseDataService.mapKenticoObject(i, r.modular_content))),
				map(res => res.map(r => new Course(r)))
			);
		}
	}

	getPageHome(): Observable<PageHome> {
		return this.httpService.get<PageHome>(`${environment.kenticoRoot}/items.json`).pipe(
			map(r => new PageHome(r))
		);
	}
}

export class KenticoObject {
	system_id: string;
	system_type: string;
	system_name: string;
	system_codename: string;
	system_last_modified: string;
}

export class KenticoAsset {
	constructor(data: any) {
		Object.assign(this, data);
	}
	name: string;
	type: string;
	size: number;
	description: string;
	url: string;
	width: number;
	height: number;
}

export class Course extends KenticoObject {
	constructor(data: any) {
		super();
		Object.assign(this, data);

		this.banner = (data.banner && data.banner.length) ? new KenticoAsset(data.banner[0]) : null;
		this.catalog_image = (data.catalog_image && data.catalog_image.length) ? new KenticoAsset(data.catalog_image[0]) : null;
		this.study_guide = (data.study_guide && data.study_guide.length) ? new KenticoAsset(data.study_guide[0]) : null;
		this.instructors = (data.instructors || []).map(i => new Instructor(i));
		this.lectures = (data.lectures || []).map(i => new CourseLecture(i));
		this.publication_date = data.publication_date ? new Date(data.publication_date) : null;
	}

	publication_date: Date;
	title: string;
	url_slug: string;
	banner: KenticoAsset;
	catalog_image: KenticoAsset;
	course_trailer_id: string;
	overview: any;
	instructors: Instructor[];
	lectures: CourseLecture[];
	study_guide: KenticoAsset;
	cta_embed_code: string;

	donation_url: string;
	course_sponsor: string;

	enrolled: boolean;
}

export class Instructor extends KenticoObject {
	constructor(data: any) {
		super();
		Object.assign(this, data);
		this.profile_photo = (data.profile_photo && data.profile_photo.length) ? new KenticoAsset(data.profile_photo[0]) : null;
	}
	prefix: string;
	first_name: string;
	middle_name: string;
	last_name: string;
	suffix: string;
	bio: any;
	profile_photo: KenticoAsset;
}

export class CourseContent extends KenticoObject {
	title: string;
	url_slug: string;
}

export class CourseLecture extends CourseContent {
	constructor(data: any) {
		super();
		Object.assign(this, data);
		this.study_guide = (data.study_guide && data.study_guide.length) ? new KenticoAsset(data.study_guide[0]) : null;
		this.recommended_readings = (data.recommended_readings || []).map(rr => new KenticoAsset(rr));
		this.instructors = (data.instructors || []).map(i => new Instructor(i));
		this.supplementary_videos = (data.supplementary_videos || []).map(i => new SupplementalVideo(i));
	}
	study_guide: KenticoAsset;
	url_slug: string;
	overview: any;
	duration: string;
	video_id: string;

	recommended_readings: KenticoAsset[];
	instructors: Instructor[];
	supplementary_videos: SupplementalVideo[];
}

export class SupplementalVideo extends CourseContent {
	constructor(data: any) {
		super();
		Object.assign(this, data);
	}
	url_slug: string;
	overview: any;
	duration: string;
	video_id: string;
}

export class PageHome extends KenticoObject {
	constructor(data: any) {
		super();
		Object.assign(this, data);

		this.banner_background = (data.banner_background && data.banner_background.length) ? new KenticoAsset(data.banner_background[0]) : null;
	}

	meta_description: string;
	banner_background: KenticoAsset;
	introduction: string;
}
